import { InvoicePositionsState } from './types';
import { ActionTree } from 'vuex';
import { RootState } from '../../types';
import { Logger } from 'fsts';
import { DefaultBackendInvoicePosition } from '@/shared/backend/invoicePosition';
import { InvoicePosition, Value } from '@/shared/model/invoicePosition';
import { AxiosResponse } from 'axios';
import moduleUtils from '../moduleUtils';
import { i18nGlobal } from '@/i18n';
import { InvoicePaymentStatus } from '@/shared/model/invoice';
import DownloadUtils from '@/shared/utils/downloadUtils';
import { Attendee } from '@/shared/model/attendee';
import { AttendeeFixedCost } from '@/shared/model/attendeeFixedCost';
import { ReferenceParent } from '@/shared/model/referenceParent';

const logger = new Logger('actions.invoices');
export const actions: ActionTree<InvoicePositionsState, RootState> = {
  getInvoicePositions(
    { commit, getters },
    payload?: {
      searchParams?: any;
      invoiceId?: string;
      attendeeAppUserId?: string;
      filterByStatuses?: InvoicePaymentStatus[];
      locationId?: string;
      departmentId?: string;
      dateFrom?: string;
      dateTill?: string;
    }
  ) {
    commit('setInvoicePositionsIsLoading', true);
    let searchParams =
      payload?.searchParams ?? getters.getInvoicePositionsSearchParams;

    return DefaultBackendInvoicePosition.getInvoicePositions(
      searchParams,
      payload?.invoiceId,
      payload?.attendeeAppUserId,
      payload?.filterByStatuses,
      payload?.locationId,
      payload?.departmentId,
      payload?.dateFrom,
      payload?.dateTill
    )
      .then((response: AxiosResponse<Value>) => {
        commit('setInvoicePositions', response.data);
        return response.data;
      })
      .catch((e: any) => {
        moduleUtils.error('error', commit, e, logger);
        throw e;
      })
      .finally(() => {
        commit('setInvoicePositionsIsLoading', false);
      });
  },
  getInvoicePositionsForActivatedAttendee(
    { commit },
    payload: {
      attendee: Attendee;
      attendeeFixedCosts?: AttendeeFixedCost[];
      departmentIds?: string[];
    }
  ) {
    return DefaultBackendInvoicePosition.getInvoicePositionsForActivatedAttendee(
      payload.attendee,
      payload.attendeeFixedCosts,
      payload.departmentIds,
      i18nGlobal.locale.value
    )
      .then((response: AxiosResponse<InvoicePosition[]>) => response.data)
      .catch((e: any) => {
        moduleUtils.error('error', commit, e, logger);
        throw e;
      });
  },
  getInvoicePositionsByMembershipTypeSubscriptions(
    { commit },
    payload: {
      attendeeAppUserId: string;
      membershipTypeSubscriptions: ReferenceParent[];
    }
  ) {
    return DefaultBackendInvoicePosition.getInvoicePositionsByMembershipTypeSubscriptions(
      payload.attendeeAppUserId,
      payload.membershipTypeSubscriptions,
      i18nGlobal.locale.value
    )
      .then((response: AxiosResponse<InvoicePosition[]>) => response.data)
      .catch((e: any) => {
        moduleUtils.error('error', commit, e, logger);
        throw e;
      });
  },
  addInvoicePosition({ commit }, invoicePosition: InvoicePosition) {
    const culture = i18nGlobal.locale.value === 'de' ? 'de-DE' : 'en-US';
    return DefaultBackendInvoicePosition.addInvoicePosition(
      invoicePosition,
      culture
    )
      .then(
        (
          response: AxiosResponse<{
            errors: [];
            id: string;
            isValid: boolean;
          }>
        ) => {
          moduleUtils.ok(i18nGlobal.t(`success.invoiceposition_add`), commit);
          return response.data;
        }
      )
      .catch((e: any) => {
        moduleUtils.error('error', commit, e, logger);
        throw e;
      });
  },
  addInvoicePositionsByCosts(
    { commit },
    payload: {
      attendeeAppUserId: string;
      costIds: string[];
      costEntries: { costId: string; forAttendeeId?: string }[];
      customCostEntries: {
        euroCost: number;
        description: string;
        forAttendeeId?: string;
      }[];
      singleInvoice?: boolean;
      paymentStatus?: InvoicePaymentStatus;
    }
  ) {
    const culture = i18nGlobal.locale.value == 'de' ? 'de-DE' : 'en-US';
    return DefaultBackendInvoicePosition.addInvoicePositionsByCosts(
      payload.attendeeAppUserId,
      payload.costEntries,
      payload.customCostEntries,
      payload.singleInvoice ?? false,
      payload.paymentStatus ?? InvoicePaymentStatus.Open,
      culture
    )
      .then(
        (
          response: AxiosResponse<{
            errors: [];
            id: string;
            isValid: boolean;
          }>
        ) => {
          moduleUtils.ok(i18nGlobal.t(`success.invoiceposition_add`), commit);
          return response.data;
        }
      )
      .catch((e: any) => {
        moduleUtils.error('error', commit, e, logger);
        throw e;
      });
  },
  addInvoicePositionsForActivatedAttendee(
    { commit },
    payload: {
      attendeeId: string;
      membershipTypeAttendees: ReferenceParent[];
      attendeeFixedCosts: AttendeeFixedCost[];
      departmentIds: string[];
    }
  ) {
    return DefaultBackendInvoicePosition.addInvoicePositionsForActivatedAttendee(
      payload.attendeeId,
      payload.membershipTypeAttendees,
      payload.attendeeFixedCosts,
      payload.departmentIds,
      i18nGlobal.locale.value
    )
      .then(
        (
          response: AxiosResponse<{
            errors: [];
            id: string;
            isValid: boolean;
          }>
        ) => {
          moduleUtils.ok(i18nGlobal.t(`success.invoiceposition_add`), commit);
          return response.data;
        }
      )
      .catch((e: any) => {
        moduleUtils.error('error', commit, e, logger);
        throw e;
      });
  },
  setInvoicePositionCanceled(
    { commit },
    payload: { invoicePositionId: string; canceled: boolean }
  ) {
    let backendCall = payload.canceled
      ? DefaultBackendInvoicePosition.cancelInvoicePosition(
          payload.invoicePositionId
        )
      : DefaultBackendInvoicePosition.unCancelInvoicePosition(
          payload.invoicePositionId
        );

    return backendCall
      .then(
        (
          response: AxiosResponse<{
            errors: [];
            id: string;
            isValid: boolean;
          }>
        ) => {
          moduleUtils.ok(
            i18nGlobal.t(
              payload.canceled
                ? `success.invoiceposition_cancel`
                : `success.invoiceposition_uncancel`
            ),
            commit
          );
          return response.data;
        }
      )
      .catch((e: any) => {
        moduleUtils.error('error', commit, e, logger);
        throw e;
      });
  },
  updateInvoicePosition({ commit }, invoicePosition: InvoicePosition) {
    return DefaultBackendInvoicePosition.updateInvoicePosition(invoicePosition)
      .then(
        (
          response: AxiosResponse<{
            errors: [];
            id: string;
            isValid: boolean;
          }>
        ) => {
          moduleUtils.ok(i18nGlobal.t('success.invoiceposition_edit'), commit);
          return response.data;
        }
      )
      .catch((e: any) => {
        moduleUtils.error('error', commit, e, logger);
        throw e;
      });
  },
  exportInvoicePositions(
    { commit },
    payload: {
      filterByMembershipTypeIds: string[];
      filterByCourseIds: string[];
      filterByCostIds: string[];
      filterByAttendeeIds: string[];
      filterByPaymentStatuses: InvoicePaymentStatus[];
      fromDate: string | undefined;
      tillDate: string | undefined;
    }
  ) {
    const culture = i18nGlobal.locale.value == 'de' ? 'de-DE' : 'en-US';
    commit('setInvoicePositionsIsLoading', true);
    return DefaultBackendInvoicePosition.exportInvoicePositions(
      payload.filterByMembershipTypeIds,
      payload.filterByCourseIds,
      payload.filterByCostIds,
      payload.filterByAttendeeIds,
      payload.filterByPaymentStatuses,
      payload.fromDate,
      payload.tillDate,
      culture
    )
      .then(DownloadUtils.openDownloadedFileInBrowser)
      .catch((e: any) => {
        moduleUtils.error('error', commit, e, logger);
        throw e;
      })
      .finally(() => {
        commit('setInvoicePositionsIsLoading', false);
      });
  },
};
